function formatPrice(cents) {
  const value = (Number(cents) || 0) / 100;
  const currency = window?.Shopify?.currency?.active || 'EUR';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(value);
}

function getExistingDiscountCodes() {
  const badges = document.querySelectorAll('[data-ref="discount-badge"]');
  const codes = [];
  badges.forEach((badge) => {
    if (!(badge instanceof HTMLElement)) return;
    const code = badge.dataset.discountCode;
    if (typeof code === 'string' && code.trim() !== '') {
      codes.push(code.trim());
    }
  });
  return [...new Set(codes)];
}

async function applyDiscountCodeToCart(discountCode = '') {
  const code = typeof discountCode === 'string' ? discountCode.trim() : '';
  if (!code) return null;

  const cartDrawer = document.querySelector('cart-drawer');
  const sections = cartDrawer?.dataset?.sectionId ? [cartDrawer.dataset.sectionId] : [];
  const existingDiscounts = getExistingDiscountCodes();
  const mergedDiscounts = [...new Set([...existingDiscounts, code])];

  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      discount: mergedDiscounts.join(','),
      sections: sections.join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) {
    throw new Error('Impossible dappliquer le code promo');
  }

  const data = await response.json();
  const failedDiscount = data.discount_codes?.find((discount) => discount.code === code && discount.applicable === false);
  if (failedDiscount) {
    document.dispatchEvent(
      new CustomEvent('toast:open', {
        detail: { type: 'error', message: 'Le code promo nest pas applicable pour ce panier.' },
      }),
    );
    return null;
  }

  return data;
}

async function addPackToCart(discountCode = '', formData = null) {
  try {
    const body = formData || { items: [] };
    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer?.dataset?.sectionId ? [cartDrawer.dataset.sectionId] : [];
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error('Impossible dajouter la selection au panier');
    }

    const responseData = await response.json();
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          resource: {},
          data: {
            itemCount: responseData.item_count,
            source: 'pack-builder',
            sections: responseData.sections,
            discountCode,
          },
        },
      }),
    );

    const discountData = await applyDiscountCodeToCart(discountCode);
    if (discountData?.sections) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            resource: {},
            data: {
              itemCount: discountData.item_count,
              source: 'pack-builder-discount',
              sections: discountData.sections,
              discountCode,
            },
          },
        }),
      );
    }

    document.dispatchEvent(new Event('cart:open', { bubbles: true }));
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

if (!customElements.get('deployer-pack-builder')) {
  customElements.define(
    'deployer-pack-builder',
    class PackBuilder extends HTMLElement {
      connectedCallback() {
        this.clickHandler = (event) => {
          const quickAddButton = event.target.closest('.quick-add-button[data-variant-id]');
          const legacyAddButton = event.target.closest('.addtocart');
          if (!quickAddButton && !legacyAddButton) return;
          event.preventDefault();
          event.stopPropagation();
          this.dispatchVariantData(event, quickAddButton, legacyAddButton);
        };
        this.addEventListener('click', this.clickHandler, true);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.clickHandler, true);
      }

      async dispatchVariantData(event, quickAddButton, legacyAddButton) {
        this.handleAddToCartText(event);
        let variantId = null;
        let handle = null;

        if (quickAddButton) {
          variantId = quickAddButton.dataset.variantId;
          const productCard = quickAddButton.closest('product-card');
          const productUrl = productCard?.dataset?.productUrl;
          if (productUrl) {
            const cleanPath = productUrl.split('?')[0].split('#')[0];
            const parts = cleanPath.split('/').filter(Boolean);
            handle = parts[parts.length - 1];
          }
        }

        if (legacyAddButton && (!variantId || !handle)) {
          const content = event.target.closest('.boxproduct--content');
          const variantInput = content?.querySelector('input[name="id"]');
          if (variantInput) {
            variantId = variantInput.value;
            handle = variantInput.dataset.handle;
          }
        }

        if (!variantId || !handle) return;
        const variantData = await this.getVariantData(handle, variantId);
        if (!variantData) return;

        document.dispatchEvent(
          new CustomEvent('pack-builder-selected-variants-changed', {
            detail: variantData,
          }),
        );
      }

      handleAddToCartText(event) {
        const quickAddButton = event.target.closest('.quick-add-button');
        if (quickAddButton) {
          quickAddButton.classList.add('is-loading');
          setTimeout(() => quickAddButton.classList.remove('is-loading'), 600);
          return;
        }

        const addToCart = event.target.closest('.addtocart');
        if (!addToCart) return;

        const addToCartText = addToCart.querySelector('.addtocart--text--basic');
        const addToCartTextSuccess = addToCart.querySelector('.addtocart--text--success');
        if (!addToCartText || !addToCartTextSuccess) return;

        addToCartText.style.display = 'none';
        addToCartText.style.opacity = '0';
        addToCartTextSuccess.style.display = 'block';
        addToCartTextSuccess.style.opacity = '1';

        setTimeout(() => {
          addToCartText.style.display = 'inline-block';
          addToCartText.style.opacity = '1';
          addToCartTextSuccess.style.display = 'none';
          addToCartTextSuccess.style.opacity = '0';
        }, 1000);
      }

      async getVariantData(productHandle, variantId) {
        try {
          const response = await fetch(`/products/${productHandle}.js`);
          const data = await response.json();
          const parsedVariantId = parseInt(variantId, 10);
          const variant = data.variants.find((item) => item.id === parsedVariantId);
          if (!variant) return null;

          if (!variant.featured_image) {
            variant.featured_image = {
              src: data.featured_image || '',
              alt: data.title,
            };
          }

          variant.name = data.title;
          return variant;
        } catch (error) {
          console.error('Error:', error);
          return null;
        }
      }
    },
  );
}

if (!customElements.get('pack-builder-summary')) {
  customElements.define(
    'pack-builder-summary',
    class PackBuilderSummary extends HTMLElement {
      constructor() {
        super();
        this.summaryProductList = this.querySelector('.pack-builder-summary--product-list');
        this.productsCount = parseInt(this.dataset.productsCount || '0', 10);
        this.minProduct = parseInt(this.dataset.minProduct || '0', 10);
        this.submitForm = this.querySelector('button[name="add"]');
        this.form = this.querySelector('form');
        this.thresholds = [];
        this.discounts = [];
        this.totalPrice = 0;
        this.discountCode = '';
        this.onVariantSelected = null;
        this.onTotalPriceChanged = null;
        this.onSubmitClick = null;
        this.steps = this.closest('section')?.querySelectorAll('.pack-builder-progress-bar--item') || [];

        this.steps.forEach((step) => {
          this.thresholds.push(parseInt(step.dataset.threshold, 10) * 100);
          this.discounts.push(parseInt(step.dataset.discount, 10) * 100);
        });
      }

      connectedCallback() {
        this.storageKey = `pack-builder-summary:${window.location.pathname}:${this.closest('section')?.id || 'default'}`;
        this.onVariantSelected = (event) => {
          const selectedVariant = event.detail;
          if (!selectedVariant) return;
          this.createVariantItem(selectedVariant);
          this.increaseProductCount();
        };
        document.addEventListener('pack-builder-selected-variants-changed', this.onVariantSelected);

        this.onTotalPriceChanged = (event) => {
          this.updatePrices(event.detail);
          this.updateSummaryText(event.detail);
          this.getDiscountCode(event.detail.thresholdIndex);
          this.handleSavingsState(event.detail.thresholdIndex);
        };
        document.addEventListener('pack-builder-total-price-changed', this.onTotalPriceChanged);

        this.restoreState();

        if (this.submitForm) {
          this.onSubmitClick = async (event) => {
            event.preventDefault();
            this.updateFormInputs();
            const added = await addPackToCart(this.discountCode, this.collectItemsPayload());
            if (added) {
              this.clearSummaryState();
            }
          };
          this.submitForm.addEventListener('click', this.onSubmitClick);
        }
      }

      disconnectedCallback() {
        if (this.onVariantSelected) {
          document.removeEventListener('pack-builder-selected-variants-changed', this.onVariantSelected);
        }
        if (this.onTotalPriceChanged) {
          document.removeEventListener('pack-builder-total-price-changed', this.onTotalPriceChanged);
        }
        if (this.submitForm && this.onSubmitClick) {
          this.submitForm.removeEventListener('click', this.onSubmitClick);
        }
      }

      clearSummaryState() {
        this.summaryProductList.innerHTML = '';
        this.productsCount = 0;
        this.updateProductCountDisplay();
        this.handleSummaryTextState();
        this.handleButtonState();
        this.handleTotalsState(0);
        this.getTotalPrice();
        this.saveState();
      }

      updateProductCountDisplay() {
        const countElement = this.querySelector('.pack-builder-summary--title-product-count');
        if (countElement) {
          countElement.innerHTML = `${this.productsCount}`;
        }
      }

      collectItemsPayload() {
        const items = [];
        this.querySelectorAll('.pack-builder-summary--product-list--item').forEach((item) => {
          items.push({
            id: parseInt(item.dataset.id, 10),
            quantity: parseInt(item.dataset.quantity, 10),
          });
        });
        return { items };
      }

      createVariantItem(variant, crosssell = null) {
        const existingItem = this.summaryProductList.querySelector(`[data-id="${variant.id}"]`);
        if (existingItem) {
          const quantityElement = existingItem.querySelector('.pack-builder-summary--product-list--item--quantity');
          const quantity = parseInt(quantityElement.innerHTML, 10);
          quantityElement.innerHTML = quantity + 1;
          existingItem.dataset.quantity = quantity + 1;
          return;
        }

        const imageSrc = variant?.featured_image?.src || '';
        const imageAlt = variant?.featured_image?.alt || variant?.name || variant?.title || '';
        const productTitle = variant?.name || variant?.product_title || variant?.title || '';
        const rawVariantTitle = variant?.title || '';
        const variantTitle =
          rawVariantTitle &&
          rawVariantTitle.toLowerCase() !== 'default title' &&
          rawVariantTitle !== productTitle
            ? rawVariantTitle
            : '';
        const item = document.createElement('li');
        item.dataset.id = variant.id;
        item.dataset.price = variant.price;
        item.dataset.quantity = 1;
        item.dataset.compareAtPrice = variant.compare_at_price == null ? 0 : variant.compare_at_price;
        item.classList.add('pack-builder-summary--product-list--item');
        item.innerHTML = `
          <div class="pack-builder-summary--product-list--item--content">
            <div class="pack-builder-summary--product-list--item--image">
              <span class="pack-builder-summary--product-list--item--quantity">1</span>
              <img src="${imageSrc}" alt="${imageAlt}" width="60" height="60" />
            </div>
            <div class="pack-builder-summary--product-list--item--title">
              <div class="pack-builder-summary--product-list--item--title-main">${productTitle}</div>
              ${
                variantTitle
                  ? `<div class="pack-builder-summary--product-list--item--title-variant">${variantTitle}</div>`
                  : ''
              }
            </div>
          </div>
          <div class="pack-builder-summary--product-list--item--remove" data-id="${variant.id}">
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#000000"><path d="M256-192.35 192.35-256l224-224-224-224L256-767.65l224 224 224-224L767.65-704l-224 224 224 224L704-192.35l-224-224-224 224Z"/></svg>
          </div>
        `;
        this.summaryProductList.appendChild(item);

        item.querySelector('.pack-builder-summary--product-list--item--remove').addEventListener('click', (event) => {
          this.removeItem(event.currentTarget.dataset.id);
          if (crosssell != null) {
            const input = crosssell.querySelector('.deployer-switch input');
            if (input) input.checked = false;
            crosssell.classList.remove('toggle-cross-sell__product--active');
          }
        });
      }

      removeItem(variantId) {
        const row = this.summaryProductList.querySelector(`.pack-builder-summary--product-list--item[data-id="${variantId}"]`);
        if (!row) return;

        const quantity = row.querySelector('.pack-builder-summary--product-list--item--quantity').innerHTML;
        this.decreaseProductCount(quantity);
        row.remove();
        this.getTotalPrice();
        this.saveState();
      }

      decreaseProductCount(value) {
        this.productsCount = parseInt(this.productsCount, 10) - parseInt(value, 10);
        this.updateProductCountDisplay();
        this.handleSummaryTextState();
        this.handleButtonState();
        this.handleTotalsState(this.productsCount);
      }

      increaseProductCount() {
        this.productsCount = parseInt(this.productsCount, 10) + 1;
        this.updateProductCountDisplay();
        this.handleSummaryTextState();
        this.handleButtonState();
        this.getTotalPrice();
        this.handleTotalsState(this.productsCount);
        this.saveState();
      }

      saveState() {
        if (!this.storageKey) return;
        try {
          const items = Array.from(this.querySelectorAll('.pack-builder-summary--product-list--item')).map((item) => {
            const image = item.querySelector('.pack-builder-summary--product-list--item--image img');
            const titleMain = item.querySelector('.pack-builder-summary--product-list--item--title-main');
            const titleVariant = item.querySelector('.pack-builder-summary--product-list--item--title-variant');
            return {
              id: parseInt(item.dataset.id, 10),
              price: parseInt(item.dataset.price, 10) || 0,
              compareAtPrice: parseInt(item.dataset.compareAtPrice, 10) || 0,
              quantity: parseInt(item.dataset.quantity, 10) || 1,
              imageSrc: image?.getAttribute('src') || '',
              imageAlt: image?.getAttribute('alt') || '',
              productTitle: titleMain?.textContent?.trim() || '',
              variantTitle: titleVariant?.textContent?.trim() || '',
            };
          });

          if (items.length === 0) {
            localStorage.removeItem(this.storageKey);
            return;
          }
          localStorage.setItem(this.storageKey, JSON.stringify(items));
        } catch (error) {
          console.error('Error while saving pack-builder state:', error);
        }
      }

      restoreState() {
        if (!this.storageKey) return;
        try {
          const raw = localStorage.getItem(this.storageKey);
          if (!raw) return;

          const items = JSON.parse(raw);
          if (!Array.isArray(items) || items.length === 0) return;

          this.summaryProductList.innerHTML = '';
          let restoredCount = 0;

          items.forEach((savedItem) => {
            const item = document.createElement('li');
            item.dataset.id = savedItem.id;
            item.dataset.price = savedItem.price;
            item.dataset.quantity = savedItem.quantity;
            item.dataset.compareAtPrice = savedItem.compareAtPrice;
            item.classList.add('pack-builder-summary--product-list--item');
            item.innerHTML = `
              <div class="pack-builder-summary--product-list--item--content">
                <div class="pack-builder-summary--product-list--item--image">
                  <span class="pack-builder-summary--product-list--item--quantity">${savedItem.quantity}</span>
                  <img src="${savedItem.imageSrc}" alt="${savedItem.imageAlt}" width="60" height="60" />
                </div>
                <div class="pack-builder-summary--product-list--item--title">
                  <div class="pack-builder-summary--product-list--item--title-main">${savedItem.productTitle || ''}</div>
                  ${
                    savedItem.variantTitle
                      ? `<div class="pack-builder-summary--product-list--item--title-variant">${savedItem.variantTitle}</div>`
                      : ''
                  }
                </div>
              </div>
              <div class="pack-builder-summary--product-list--item--remove" data-id="${savedItem.id}">
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#000000"><path d="M256-192.35 192.35-256l224-224-224-224L256-767.65l224 224 224-224L767.65-704l-224 224 224 224L704-192.35l-224-224-224 224Z"/></svg>
              </div>
            `;
            this.summaryProductList.appendChild(item);
            restoredCount += parseInt(savedItem.quantity, 10) || 1;

            item.querySelector('.pack-builder-summary--product-list--item--remove').addEventListener('click', (event) => {
              this.removeItem(event.currentTarget.dataset.id);
            });
          });

          this.productsCount = restoredCount;
          this.updateProductCountDisplay();
          this.handleSummaryTextState();
          this.handleButtonState();
          this.handleTotalsState(this.productsCount);
          this.getTotalPrice();
        } catch (error) {
          console.error('Error while restoring pack-builder state:', error);
        }
      }

      handleSummaryTextState() {
        const summaryText = this.querySelector('.pack-builder-summary--text');
        const disabledText = this.querySelector('.pack-builder-summary--disabled-text');
        const productsNeeded = this.querySelector('.pack-builder-summary--disabled-text-products-needed');

        if (this.productsCount >= this.minProduct) {
          summaryText?.classList.remove('hidden');
          disabledText?.classList.add('hidden');
        } else {
          if (productsNeeded) {
            productsNeeded.innerHTML = `${this.minProduct - this.productsCount}`;
          }
          summaryText?.classList.add('hidden');
          disabledText?.classList.remove('hidden');
        }
      }

      handleButtonState() {
        const addButton = this.querySelector('.pack-builder-summary--add');
        const addText = this.querySelector('.pack-builder-summary--add-text');
        const addTextDisabled = this.querySelector('.pack-builder-summary--add-text--disabled');
        if (!addButton) return;

        if (this.productsCount >= this.minProduct) {
          addButton.disabled = false;
          addText?.classList.remove('hidden');
          addTextDisabled?.classList.add('hidden');
        } else {
          addButton.disabled = true;
          addText?.classList.add('hidden');
          addTextDisabled?.classList.remove('hidden');
        }
      }

      handleTotalsState(productCount) {
        if (productCount > 0) {
          this.querySelector('.pack-builder-summary--totals').classList.remove('hidden');
        } else {
          this.querySelector('.pack-builder-summary--totals').classList.add('hidden');
        }
      }

      handleSavingsState(thresholdIndex) {
        if (thresholdIndex !== null) {
          this.querySelector('.pack-builder-summary--savings').classList.remove('hidden');
          this.updateSavingsValue(this.discounts[thresholdIndex]);
        } else {
          this.querySelector('.pack-builder-summary--savings').classList.add('hidden');
        }
      }

      getTotalPrice() {
        const items = Array.from(this.querySelectorAll('.pack-builder-summary--product-list--item'));
        this.totalPrice = items.reduce((total, item) => total + parseFloat(item.dataset.price * item.dataset.quantity), 0);
        const thresholdIndex = this.getThresholdIndex(this.totalPrice);
        const nextThresholdIndex = this.getNextThresholdIndex(this.totalPrice);

        document.dispatchEvent(
          new CustomEvent('pack-builder-total-price-changed', {
            detail: {
              totalPrice: this.totalPrice,
              thresholdIndex,
              nextThresholdIndex,
            },
          }),
        );
      }

      getThresholdIndex(totalPrice) {
        let lastIndex = null;
        this.thresholds.forEach((threshold, index) => {
          if (totalPrice >= threshold) {
            lastIndex = index;
          }
        });
        return lastIndex;
      }

      getNextThresholdIndex(totalPrice) {
        let lastIndex = -1;
        this.thresholds.forEach((threshold, index) => {
          if (totalPrice >= threshold) {
            lastIndex = index;
          }
        });
        return lastIndex + 1;
      }

      updatePrices(data) {
        let totalPrice = data.totalPrice;
        if (data.thresholdIndex !== null) {
          this.querySelector('.pack-builder-summary--compare').innerHTML = formatPrice(totalPrice);
          totalPrice -= this.discounts[data.thresholdIndex];
        } else {
          this.querySelector('.pack-builder-summary--compare').innerHTML = '';
        }

        this.querySelector('.pack-builder-summary--price').innerHTML = formatPrice(totalPrice);
      }

      updateSavingsValue(savings) {
        this.querySelector('.pack-builder-summary--savings-value').innerHTML = formatPrice(savings);
      }

      updateSummaryText(data) {
        const amountNeededSpans = this.querySelectorAll('.pack-builder-summary--text-amount-needed');
        const discountSpans = this.querySelectorAll('.pack-builder-summary--text-discount');
        const nextThreshold = this.thresholds[data.nextThresholdIndex];
        const nextDiscount = this.discounts[data.nextThresholdIndex];
        const successText = this.querySelector('.pack-builder-summary--success-text');
        const summaryText = this.querySelector('.pack-builder-summary--text');
        const disabledText = this.querySelector('.pack-builder-summary--disabled-text');

        if (typeof nextThreshold !== 'undefined') {
          amountNeededSpans.forEach((amountNeededSpan) => {
            amountNeededSpan.innerHTML = formatPrice(nextThreshold - data.totalPrice);
          });
        }
        if (typeof nextDiscount !== 'undefined') {
          discountSpans.forEach((discountSpan) => {
            discountSpan.innerHTML = formatPrice(nextDiscount);
          });
        }

        if (data.nextThresholdIndex === this.thresholds.length) {
          successText?.classList.remove('hidden');
          summaryText?.classList.add('hidden');
          disabledText?.classList.add('hidden');
        } else if (this.productsCount >= this.minProduct) {
          successText?.classList.add('hidden');
          summaryText?.classList.remove('hidden');
          disabledText?.classList.add('hidden');
        }
      }

      updateFormInputs() {
        if (!this.form) return;
        this.resetMainFormInputs();
        const items = Array.from(this.querySelectorAll('.pack-builder-summary--product-list--item'));
        items.forEach((item, index) => {
          const inputId = document.createElement('input');
          inputId.type = 'hidden';
          inputId.name = `items[${index + 1}][id]`;
          inputId.value = item.dataset.id;
          const inputQuantity = document.createElement('input');
          inputQuantity.type = 'hidden';
          inputQuantity.name = `items[${index + 1}][quantity]`;
          inputQuantity.value = item.dataset.quantity;
          this.form.querySelector('.pack-builder-summary--form--inputs').appendChild(inputId);
          this.form.querySelector('.pack-builder-summary--form--inputs').appendChild(inputQuantity);
        });
      }

      resetMainFormInputs() {
        if (!this.form) return;
        this.form.querySelectorAll('input').forEach((input) => input.remove());
      }

      getDiscountCode(thresholdIndex) {
        if (thresholdIndex !== null && this.steps[thresholdIndex]) {
          this.discountCode = this.steps[thresholdIndex].dataset.discountCode;
        } else {
          this.discountCode = '';
        }
      }
    },
  );
}

if (!customElements.get('pack-builder-toggle-cross-sell')) {
  customElements.define(
    'pack-builder-toggle-cross-sell',
    class PackBuilderToggleCrossSell extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('.toggle-cross-sell__product').forEach((product) => {
          const switchLabel = product.querySelector('.deployer-switch--label');
          const select = product.querySelector('.deployer-select');
          if (switchLabel) {
            switchLabel.addEventListener('click', (event) => {
              if (event.target.tagName !== 'INPUT') {
                this.onToggleProduct(event, product);
              }
            });
          }
          if (select) {
            select.addEventListener('change', (event) => this.variantChanged(event, product));
          }
        });
      }

      async onToggleProduct(event, productElement) {
        event.preventDefault();
        const checkbox = productElement.querySelector('input[type="checkbox"]');
        if (!checkbox) return;

        const isChecked = checkbox.checked;
        if (!isChecked) {
          checkbox.checked = true;
          productElement.classList.add('toggle-cross-sell__product--active');

          const variantData = await this.closest('section').querySelector('deployer-pack-builder').getVariantData(
            checkbox.dataset.productHandle,
            checkbox.dataset.variantId,
          );

          const summary = this.closest('section').querySelector('pack-builder-summary');
          summary.createVariantItem(variantData, productElement);
          summary.increaseProductCount();
        } else {
          checkbox.checked = false;
          productElement.classList.remove('toggle-cross-sell__product--active');
          this.closest('section').querySelector('pack-builder-summary').removeItem(checkbox.dataset.variantId);
        }
      }

      variantChanged(event, product) {
        const select = product.querySelector('.deployer-select');
        if (!select) return;
        const optionSelected = select.options[select.selectedIndex];
        this.updateProductPrice(product, optionSelected);
        this.updateProductImage(product, optionSelected);
        this.updateVariantId(product, optionSelected, event);
      }

      updateProductPrice(product, optionSelected) {
        const variantPrice = optionSelected.dataset.price;
        product.querySelector('.toggle-cross-sell__product-price').innerHTML = variantPrice;

        const compareAt = product.querySelector('.toggle-cross-sell__product-compare-at-price');
        const badge = product.querySelector('.toggle-cross-sell__product-badge-discount');
        if (optionSelected.dataset.compareAtPrice) {
          if (compareAt) {
            compareAt.innerHTML = optionSelected.dataset.compareAtPrice;
            compareAt.style.display = 'block';
          }
          if (badge) {
            badge.innerHTML = optionSelected.dataset.discountPercentage;
            badge.style.display = 'inline-flex';
          }
        } else {
          if (compareAt) compareAt.style.display = 'none';
          if (badge) badge.style.display = 'none';
        }
      }

      updateProductImage(product, optionSelected) {
        if (optionSelected.dataset.variantImage && product.querySelector('.toggle-cross-sell__product-image img')) {
          product.querySelector('.toggle-cross-sell__product-image img').src = optionSelected.dataset.variantImage;
        }
      }

      updateVariantId(product, optionSelected, event) {
        const variantId = optionSelected.value;
        const switchInput = product.querySelector('.deployer-switch input');
        if (!switchInput) return;
        switchInput.dataset.variantId = variantId;
        if (!switchInput.checked) {
          this.onToggleProduct(event, product);
        }
      }
    },
  );
}

if (!customElements.get('deployer-pack-builder-progress-bar')) {
  customElements.define(
    'deployer-pack-builder-progress-bar',
    class DeployerPackBuilderProgressBar extends HTMLElement {
      constructor() {
        super();
        this.thresholds = [];
        this.steps = this.querySelectorAll('.pack-builder-progress-bar--item');
        this.discounts = [];
        this.topStepsLabels = this.querySelectorAll('.pack-builder-progress-bar--top-item');
        this.lastTotalPrice = 0;
        this.steps.forEach((step) => {
          this.thresholds.push(parseInt(step.dataset.threshold, 10) * 100);
          this.discounts.push(parseInt(step.dataset.discount, 10) * 100);
        });
      }

      connectedCallback() {
        if (this.thresholds.length > 0) {
          this.lastTotalPrice = 0;
          this.updateProgressBar(0);
          this.updateProgressText({
            totalPrice: 0,
            thresholdIndex: null,
            nextThresholdIndex: 0,
          });
        }

        const summary = this.closest('section')?.querySelector('pack-builder-summary');
        if (summary) {
          const items = Array.from(summary.querySelectorAll('.pack-builder-summary--product-list--item'));
          const restoredTotal = items.reduce((total, item) => {
            const price = parseInt(item.dataset.price || '0', 10);
            const quantity = parseInt(item.dataset.quantity || '0', 10);
            return total + price * quantity;
          }, 0);

          const nextThresholdIndex = this.thresholds.findIndex((threshold) => restoredTotal < threshold);
          const normalizedNextThresholdIndex = nextThresholdIndex === -1 ? this.thresholds.length : nextThresholdIndex;
          const thresholdIndex = normalizedNextThresholdIndex === 0 ? null : normalizedNextThresholdIndex - 1;
          this.lastTotalPrice = restoredTotal;
          this.updateProgressBar(restoredTotal);
          this.updateProgressText({
            totalPrice: restoredTotal,
            thresholdIndex,
            nextThresholdIndex: normalizedNextThresholdIndex,
          });
        }

        document.addEventListener('pack-builder-total-price-changed', (event) => {
          this.lastTotalPrice = event.detail.totalPrice || 0;
          this.updateProgressBar(event.detail.totalPrice);
          this.updateProgressText(event.detail);
        });

        this.resizeHandler = () => this.updateProgressBar(this.lastTotalPrice);
        window.addEventListener('resize', this.resizeHandler);
      }

      disconnectedCallback() {
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }
      }

      getStepLeft(step) {
        const isMobile = window.matchMedia('(max-width: 750px)').matches;
        const left = isMobile ? step.dataset.progressLeftMobile : step.dataset.progressLeft;
        return parseFloat(left) || 0;
      }

      updateProgressBar(totalPrice) {
        const progressBarInner = this.querySelector('.pack-builder-progress-bar--inner');
        if (!progressBarInner || this.steps.length === 0) return;

        let previousLeft = 0;
        let previousThreshold = 0;
        this.steps.forEach((step, index) => {
          step.classList.remove('completed');
          if (this.topStepsLabels[index]) this.topStepsLabels[index].classList.remove('completed');
        });

        for (let index = 0; index < this.steps.length; index += 1) {
          const step = this.steps[index];
          const topStepLabel = this.topStepsLabels[index];
          const stepLeft = this.getStepLeft(step);
          const threshold = this.thresholds[index];

          if (totalPrice < threshold) {
            const between = (totalPrice - previousThreshold) / (threshold - previousThreshold || 1);
            const relativeLeft = previousLeft + between * (stepLeft - previousLeft);
            progressBarInner.style.width = `${Math.min(relativeLeft, stepLeft)}%`;
            return;
          }

          previousLeft = stepLeft;
          previousThreshold = threshold;
          progressBarInner.style.width = `${stepLeft}%`;
          step.classList.add('completed');
          if (topStepLabel) topStepLabel.classList.add('completed');
        }

        if (totalPrice >= this.thresholds[this.thresholds.length - 1]) {
          progressBarInner.style.width = '100%';
        }
      }

      updateProgressText(data) {
        const amountNeededSpan = this.querySelector('.pack-builder-summary--text-amount-needed');
        const discountSpan = this.querySelector('.pack-builder-summary--text-discount');
        const nextThreshold = this.thresholds[data.nextThresholdIndex];
        const nextDiscount = this.discounts[data.nextThresholdIndex];

        if (amountNeededSpan && typeof nextThreshold !== 'undefined') {
          amountNeededSpan.innerHTML = formatPrice(nextThreshold - data.totalPrice);
        }
        if (discountSpan && typeof nextDiscount !== 'undefined') {
          discountSpan.innerHTML = formatPrice(nextDiscount);
        }

        if (data.nextThresholdIndex === this.thresholds.length) {
          this.querySelector('.pack-builder-progress-bar--text--success')?.classList.remove('hidden');
          this.querySelector('.pack-builder-progress-bar--text')?.classList.add('hidden');
        } else {
          this.querySelector('.pack-builder-progress-bar--text--success')?.classList.add('hidden');
          this.querySelector('.pack-builder-progress-bar--text')?.classList.remove('hidden');
        }
      }
    },
  );
}
